"use client";

import { useState, useRef, useEffect } from "react";
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  type MultiFactorResolver,
} from "firebase/auth";
import { auth } from "@/lib/firebase";
import { friendlyMfaError } from "@/lib/mfa-errors";
import Link from "next/link";

async function createSession(user: { getIdToken: () => Promise<string> }) {
  const idToken = await user.getIdToken();
  const res = await fetch("/api/auth/session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ idToken }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? "Session error");
  return data;
}

function redirectAfterLogin(accountStatus: string) {
  window.location.href = accountStatus === "approved" ? "/overview" : "/verify/status";
}

const LABEL_DISABLED_MESSAGE = "This account has been deactivated. Please contact support.";

function isLabelDisabledError(err: unknown): boolean {
  return ((err as { message?: string })?.message ?? "").includes("label_disabled");
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // MFA state
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [mfaVerificationId, setMfaVerificationId] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaSending, setMfaSending] = useState(false);
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);
  const recaptchaContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Badge is hidden app-wide (globals.css) — keep it visible on the sign-in
    // page, which satisfies Google's reCAPTCHA branding requirement.
    document.body.classList.add("show-recaptcha-badge");
    return () => {
      document.body.classList.remove("show-recaptcha-badge");
      recaptchaVerifierRef.current?.clear();
    };
  }, []);

  // Always mint a fresh RecaptchaVerifier. The invisible widget can become
  // stale after a single verify() (especially when Firebase tries to fall back
  // from reCAPTCHA Enterprise → v2 mid-flight), and a stale ref throws
  // "reCAPTCHA client element has been removed" on retry. Cheap to recreate.
  async function createFreshRecaptcha() {
    if (recaptchaVerifierRef.current) {
      try { recaptchaVerifierRef.current.clear(); } catch { /* ignore */ }
      recaptchaVerifierRef.current = null;
    }
    const verifier = new RecaptchaVerifier(auth, recaptchaContainerRef.current!, {
      size: "invisible",
    });
    recaptchaVerifierRef.current = verifier;
    return verifier;
  }

  async function handleMfaRequired(err: unknown) {
    const resolver = getMultiFactorResolver(auth, err as Parameters<typeof getMultiFactorResolver>[1]);
    // Important: do NOT setMfaResolver yet. That triggers a React re-render into
    // the MFA UI branch, which historically unmounted the recaptcha container
    // before verifyPhoneNumber could use it. We flip to the MFA UI only after
    // a verification code is actually in flight. On failure, the user stays on
    // the sign-in form with the error visible.
    setMfaSending(true);
    try {
      const recaptcha = await createFreshRecaptcha();
      const phoneHint = resolver.hints[0];
      const provider = new PhoneAuthProvider(auth);
      const verificationId = await provider.verifyPhoneNumber(
        { multiFactorHint: phoneHint, session: resolver.session },
        recaptcha
      );
      setMfaVerificationId(verificationId);
      setMfaResolver(resolver);
    } catch (err) {
      setError(friendlyMfaError(err, "login-send-code"));
    } finally {
      setMfaSending(false);
    }
  }

  async function handleMfaSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!mfaResolver || !mfaVerificationId) return;
    setMfaLoading(true);
    setError("");
    try {
      const cred = PhoneAuthProvider.credential(mfaVerificationId, mfaCode);
      const assertion = PhoneMultiFactorGenerator.assertion(cred);
      const result = await mfaResolver.resolveSignIn(assertion);
      const data = await createSession(result.user);
      redirectAfterLogin(data.accountStatus);
    } catch (err) {
      setError(isLabelDisabledError(err) ? LABEL_DISABLED_MESSAGE : friendlyMfaError(err, "login-verify-code"));
    } finally {
      setMfaLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const data = await createSession(cred.user);
      redirectAfterLogin(data.accountStatus);
    } catch (err) {
      const code = (err as { code?: string })?.code;
      if (code === "auth/multi-factor-auth-required") {
        await handleMfaRequired(err);
      } else if (code?.includes("auth/")) {
        setError("Invalid email or password.");
      } else if (isLabelDisabledError(err)) {
        setError(LABEL_DISABLED_MESSAGE);
      } else {
        setError(
          process.env.NODE_ENV === "development"
            ? `Session error: ${(err as Error).message}`
            : "Sign-in failed. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignIn() {
    setError("");
    setGoogleLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const cred = await signInWithPopup(auth, provider);
      const data = await createSession(cred.user);
      redirectAfterLogin(data.accountStatus);
    } catch (err) {
      const code = (err as { code?: string })?.code;
      const message = (err as { message?: string })?.message ?? "";
      if (code === "auth/multi-factor-auth-required") {
        await handleMfaRequired(err);
      } else if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
        // dismissed — no error
      } else if (message.includes("no_account")) {
        setError("No account found for this Google address. Please contact support.");
      } else if (isLabelDisabledError(err)) {
        setError(LABEL_DISABLED_MESSAGE);
      } else {
        setError("Google sign-in failed. Please try again.");
      }
    } finally {
      setGoogleLoading(false);
    }
  }

  // MFA verification step
  if (mfaResolver) {
    const phoneHint = mfaResolver.hints[0] as { phoneNumber?: string };
    const maskedPhone = phoneHint.phoneNumber ?? "your phone";

    return (
      <>
        <div>
          <div className="mb-6 text-center">
            <p className="text-white font-semibold mb-1">Two-Factor Authentication</p>
            <p className="text-zinc-400 text-sm">
              {mfaSending
                ? "Sending verification code…"
                : `A code was sent to ${maskedPhone}`}
            </p>
          </div>

          {mfaSending ? (
            <div className="flex justify-center py-6">
              <span className="w-6 h-6 border-2 border-zinc-600 border-t-yellow-400 rounded-full animate-spin" />
            </div>
          ) : (
            <form onSubmit={handleMfaSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-zinc-300 mb-1">Verification Code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                  maxLength={6}
                  required
                  autoFocus
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:border-yellow-400 tracking-widest text-center text-lg"
                  placeholder="• • • • • •"
                />
              </div>
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <button
                type="submit"
                disabled={mfaLoading || mfaCode.length < 6}
                className="w-full bg-yellow-400 text-black font-semibold py-2.5 rounded-lg hover:bg-yellow-300 disabled:opacity-50 transition"
              >
                {mfaLoading ? "Verifying…" : "Verify"}
              </button>
            </form>
          )}

          <button
            onClick={() => { setMfaResolver(null); setMfaCode(""); setError(""); }}
            className="mt-4 w-full text-center text-sm text-zinc-500 hover:text-white transition"
          >
            ← Back to sign in
          </button>
        </div>
        {/* Invisible reCAPTCHA anchor — always mounted across both branches so
            the verifier's DOM target stays stable when mfaResolver flips. */}
        <div ref={recaptchaContainerRef} />
      </>
    );
  }

  return (
    <div>
      <div className="mb-8 text-center">
        <p className="text-zinc-400 text-sm">Sign in to your account</p>
      </div>

      {/* Google Sign-In */}
      <button
        type="button"
        onClick={handleGoogleSignIn}
        disabled={googleLoading || loading}
        className="w-full flex items-center justify-center gap-3 bg-zinc-900 border border-zinc-700 text-white text-sm font-medium py-2.5 rounded-lg hover:bg-zinc-800 disabled:opacity-50 transition mb-4"
      >
        {googleLoading ? (
          <span className="w-4 h-4 border-2 border-zinc-500 border-t-white rounded-full animate-spin" />
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
        )}
        {googleLoading ? "Signing in…" : "Continue with Google"}
      </button>

      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 h-px bg-zinc-800" />
        <span className="text-xs text-zinc-600">or</span>
        <div className="flex-1 h-px bg-zinc-800" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm text-zinc-300 mb-1">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:border-yellow-400"
            placeholder="you@example.com"
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm text-zinc-300">Password</label>
            <Link href="/forgot-password" className="text-xs text-zinc-500 hover:text-yellow-400 transition">
              Forgot password?
            </Link>
          </div>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:border-yellow-400"
            placeholder="••••••••"
          />
        </div>
        {error && <p className="text-red-400 text-sm">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-yellow-400 text-black font-semibold py-2.5 rounded-lg hover:bg-yellow-300 disabled:opacity-50 transition"
        >
          {loading ? "Signing in…" : "Sign In"}
        </button>
      </form>
      <p className="mt-6 text-center text-sm text-zinc-500">
        Don&apos;t have an account?{" "}
        <Link href="/signup" className="text-yellow-400 hover:underline">
          Sign up
        </Link>
      </p>

      {/* Invisible reCAPTCHA anchor for MFA */}
      <div ref={recaptchaContainerRef} />
    </div>
  );
}
